import { useAuth } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  PatientRedFlagCalledRequest,
  PatientRedFlagCalledResponse,
  PatientRedFlagsResponse,
} from "../../../shared/api-types";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import {
  markCalledOutcome,
  patientRedFlagsQueryKey,
  type MarkCalledOutcome,
  type RequestFailure,
} from "@/lib/patient-red-flags-client";

// One Client's open critical clinical red flag and Mark called (NO-850), from
// NO-846's GET /api/patients/:patientId/red-flags and POST .../called. Own
// lane only (no `?rdId=`). Every read records a Clinical Read server-side.

function redFlagsPath(patientId: string): string {
  return `/api/patients/${encodeURIComponent(patientId)}/red-flags`;
}

/** `gcTime: 0` drops the flag text from the cache as soon as the page unmounts. */
export function usePatientRedFlags(patientId: string, enabled: boolean) {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: patientRedFlagsQueryKey(patientId),
    queryFn: async () =>
      (
        await authenticatedApi<{ data: PatientRedFlagsResponse }>(
          getToken,
          redFlagsPath(patientId)
        )
      ).data,
    enabled,
    gcTime: 0,
  });
}

function requestFailure(error: unknown): RequestFailure {
  if (error instanceof ApiRequestError)
    return { status: error.status, body: error.body };

  return {};
}

/**
 * Mark called for the flag the RD was shown. The returned outcome is the
 * last answer's meaning for the banner, or null before any answer.
 */
export function useMarkRedFlagCalled(patientId: string) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = patientRedFlagsQueryKey(patientId);

  function settle(outcome: MarkCalledOutcome): void {
    if (outcome === "cleared") {
      queryClient.setQueryData<PatientRedFlagsResponse>(queryKey, { openFlag: null });
    }

    if (outcome !== "failed") void queryClient.invalidateQueries({ queryKey });
  }

  const mutation = useMutation({
    mutationFn: (request: PatientRedFlagCalledRequest) =>
      authenticatedApi<{ data: PatientRedFlagCalledResponse }>(
        getToken,
        `${redFlagsPath(patientId)}/called`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(request),
        }
      ),
    onSuccess: () => settle(markCalledOutcome(null)),
    onError: (error) => settle(markCalledOutcome(requestFailure(error))),
  });

  const outcome: MarkCalledOutcome | null = mutation.isSuccess
    ? "cleared"
    : mutation.isError
      ? markCalledOutcome(requestFailure(mutation.error))
      : null;

  return { markCalled: mutation.mutate, marking: mutation.isPending, outcome };
}
